/* CommandHeaderShell — iter101.59
 *
 * Shared editorial "command bar" chrome used by every portal that
 * anchors a page in a single identity (patient, case). Presentational
 * only — no data assumptions, no fetching, no navigation. Callers pass
 * fully-formed slots and the shell composes the visual grammar:
 *
 *   Zone A (editorial identity band)
 *     ┌────────────────────────────────────────────────────────────┐
 *     │ [avatar]  Caps kicker              [assignment mini-stack] │
 *     │           H1 Name                  [right slot ─ status /  │
 *     │           demographics · suffix     action toolbar]        │
 *     └────────────────────────────────────────────────────────────┘
 *   ── hair-thin teal gradient rule ────────────────────────────────
 *   Zone B (cockpit data strip)
 *     ┌────────────────────────────────────────────────────────────┐
 *     │ cell │ cell │ cell │ cell │ cell │ cell                    │
 *     └────────────────────────────────────────────────────────────┘
 *     [optional stripe — revision, tracking, override, etc]
 *
 * Depth tokens (matches PlannerCaseCommandHeader iter101.52 spec):
 *   · Subtle teal-tinted radial wash top-left
 *   · Grain-noise SVG overlay at 1.8% opacity
 *   · Layered elevation shadow
 *   · Divider gradient between zones
 *
 * Props
 * -----
 * avatar            React node — pre-built avatar (with any hover
 *                   overlays like PatientProfile's "Add photo").
 * identityLabel     Caps kicker text ("Patient · Case", "Patient", etc).
 * name              Primary H1 string.
 * nameTitle         Optional title tooltip for the H1 (defaults to name).
 * demographics      Array of strings for the sub-line ("32 yr", "Female",
 *                   "IVQ-PID-…"). The last element is auto-rendered in
 *                   mono when it looks like an ID (`IVQ-`, `INV-`).
 * demographicsSuffix Optional React node appended to the demographics
 *                   row (e.g. "Open all cases" link or "Edit info" btn).
 * assignments       Array of { label, value, avatar?, icon?, testid?,
 *                   title? } — rendered as caps-label + inline row in
 *                   the assignment mini-stack. Skip / pass empty to
 *                   hide the stack entirely.
 * rightSlot         React node — status capsule (planner) OR action
 *                   toolbar (doctor). Positioned far-right, aligned
 *                   with the identity block.
 * chips             Array of chip descriptors for Zone B:
 *                   { icon, label, value, tone?, mono?, testid?,
 *                     title?, render? }. `render` short-circuits the
 *                   default DataCell (used for the doctor's
 *                   ComplianceChipChooser). Pass `null` / empty array
 *                   to skip Zone B entirely.
 * stripe            Optional React node rendered under the data strip
 *                   (revision stripe, tracking strip, etc).
 * testId            Data-testid root used to derive testids for the
 *                   shell + its slots (e.g. `${testId}-header`).
 */
import React from "react";


/* Small caps label shared across the header (identity kicker,
   assignment labels, data-strip column headers). Kept in one place
   so tracking + weight rhythm stays canonical. Exported so the shell's
   callers can build custom label patterns (e.g. inside a rightSlot). */
export function CapsLabel({ children, tone = "muted", className = "" }) {
    const toneCls = tone === "teal"
        ? "text-teal-800/70"
        : "text-muted-foreground/70";
    return (
        <span className={`text-[9px] font-bold tracking-[0.18em] uppercase ${toneCls} ${className}`}>
            {children}
        </span>
    );
}


/* One cell inside the cockpit data strip. Icon + caps-label above,
   bold value below. `tone` maps to a small named palette so callers
   never have to remember hex codes. Exported for callers that want
   to compose custom chip layouts inside the shell's `chips` prop. */
export function DataCell({ icon: Icon, label, value, tone = "neutral", mono = false, testid, title }) {
    const valueTone = {
        neutral: "text-foreground",
        emerald: "text-emerald-800",
        rose:    "text-rose-800",
        slate:   "text-slate-600",
        amber:   "text-amber-800",
    }[tone] || "text-foreground";
    const iconTone = {
        neutral: "text-teal-700/70",
        emerald: "text-emerald-700",
        rose:    "text-rose-700",
        slate:   "text-slate-500",
        amber:   "text-amber-700",
    }[tone] || "text-teal-700/70";
    return (
        <div className="px-3.5 py-2.5 min-w-0" title={title}>
            <div className="flex items-center gap-1 mb-1">
                <Icon size={10} weight="fill" className={iconTone} />
                <span className="text-[9px] font-bold tracking-[0.14em] uppercase text-teal-800/70">
                    {label}
                </span>
            </div>
            <div
                className={`text-[11.5px] font-semibold leading-tight truncate ${mono ? "font-mono tabular-nums" : ""} ${valueTone}`}
                data-testid={testid}
            >
                {value}
            </div>
        </div>
    );
}


function AssignmentRow({ label, value, avatar, icon: Icon, testid, title }) {
    return (
        <div className="min-w-0" title={title || `${label}: ${typeof value === "string" ? value : ""}`}>
            <CapsLabel tone="teal">{label}</CapsLabel>
            <div className="mt-0.5 flex items-center gap-2 text-[12px] text-foreground min-w-0">
                {avatar ? (
                    avatar
                ) : Icon ? (
                    <Icon size={12} weight="fill" className="text-teal-700/70 shrink-0" />
                ) : null}
                <span className="truncate font-semibold" data-testid={testid}>
                    {value}
                </span>
            </div>
        </div>
    );
}


export default function CommandHeaderShell({
    avatar,
    identityLabel,
    name,
    nameTitle,
    demographics = [],
    demographicsSuffix,
    assignments = [],
    rightSlot,
    /* iter101.61 — Status pill sits in a dedicated top-right slot
       inside Zone A, above the actions. Keeps the status glanceable
       without competing with the action toolbar for horizontal space.
       Callers pass a pre-built pill node (usually <CaseStatusBadge />). */
    statusPill,
    chips = [],
    stripe,
    testId = "command-header",
}) {
    const cols = chips.length;
    const gridCols = cols === 6
        ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6"
        : cols === 5
            ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
            : cols === 4
                ? "grid-cols-2 sm:grid-cols-4"
                : cols === 3
                    ? "grid-cols-3"
                    : cols === 2
                        ? "grid-cols-2"
                        : "grid-cols-1";

    return (
        <div
            data-testid={testId}
            className="relative rounded-2xl border border-border/70 bg-card overflow-hidden isolate"
            style={{
                boxShadow: "0 1px 0 rgba(15,23,42,0.02), 0 12px 28px -18px rgba(15,23,42,0.10)",
            }}
        >
            {/* Ambient teal wash — bleeds from top-left corner, fades into
                pure card so the identity band feels lifted without
                dominating. Kept as a positioned div (not an overlay
                image) so text underneath stays fully legible. */}
            <div
                aria-hidden
                className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 rounded-full opacity-70"
                style={{
                    background: "radial-gradient(circle at center, rgba(20,184,166,0.09) 0%, rgba(20,184,166,0.04) 40%, transparent 70%)",
                }}
            />

            {/* Grain-noise overlay — micro texture for tactile depth.
                Inline SVG data-URI so we don't need a separate asset. */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-[0.018] mix-blend-multiply"
                style={{
                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
                }}
            />

            {/* ═══════════════════════════════════════════════════════════
                ZONE A — Editorial identity band
                ─────────────────────────────────────────────────────────── */}
            <div className="relative px-5 sm:px-6 pt-4 pb-4">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
                    {/* Identity cluster — avatar + name block */}
                    <div className="flex items-center gap-3.5 min-w-0 flex-1">
                        <div className="shrink-0" data-testid={`${testId}-avatar-slot`}>
                            {avatar}
                        </div>

                        {/* Name + demographics */}
                        <div className="min-w-0 flex-1">
                            {identityLabel && (
                                <CapsLabel tone="teal" className="block">
                                    {identityLabel}
                                </CapsLabel>
                            )}
                            <h1
                                className="font-display text-[19px] sm:text-[21px] font-bold leading-tight tracking-tight truncate mt-0.5"
                                data-testid={`${testId}-name`}
                                title={nameTitle || name || ""}
                            >
                                {name || "—"}
                            </h1>
                            {(demographics.length > 0 || demographicsSuffix) && (
                                <div className="mt-1 text-[11px] text-muted-foreground flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                                    {demographics.map((d, i) => {
                                        const isIdLike = /^(IVQ-|INV-)/.test(String(d));
                                        const isLast = i === demographics.length - 1;
                                        return (
                                            <React.Fragment key={i}>
                                                {i > 0 && <span className="text-muted-foreground/40" aria-hidden>·</span>}
                                                <span className={isLast && isIdLike ? "font-mono tabular-nums" : ""}>
                                                    {d}
                                                </span>
                                            </React.Fragment>
                                        );
                                    })}
                                    {demographicsSuffix && (
                                        <>
                                            {demographics.length > 0 && (
                                                <span className="text-muted-foreground/40" aria-hidden>·</span>
                                            )}
                                            {demographicsSuffix}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Vertical divider — thin teal fade rule between
                        identity and assignment. Hidden on smaller screens
                        where the stack becomes vertical. Only shown when
                        we actually have assignments to display. */}
                    {assignments.length > 0 && (
                        <div
                            aria-hidden
                            className="hidden lg:block w-px self-stretch"
                            style={{
                                background: "linear-gradient(to bottom, transparent 0%, rgba(20,184,166,0.25) 30%, rgba(20,184,166,0.25) 70%, transparent 100%)",
                            }}
                        />
                    )}

                    {/* Assignment mini-stack — Provider + Clinic (planner)
                        OR Clinic-only (doctor viewing own case) etc. */}
                    {assignments.length > 0 && (
                        <div className={`grid gap-3 lg:gap-1.5 shrink-0 lg:min-w-[220px] lg:max-w-[260px] ${assignments.length > 1 ? "grid-cols-2 lg:grid-cols-1" : "grid-cols-1"}`}>
                            {assignments.map((a, i) => (
                                <AssignmentRow key={i} {...a} />
                            ))}
                        </div>
                    )}

                    {/* Right cluster — stacks the status pill (if any)
                        on top of the action toolbar. Both are optional;
                        the cluster collapses cleanly when neither is
                        provided. */}
                    {(rightSlot || statusPill) && (
                        <div
                            className="shrink-0 flex flex-col items-stretch lg:items-end gap-2 lg:min-w-[200px]"
                            data-testid={`${testId}-right-cluster`}
                        >
                            {statusPill && (
                                <div
                                    className="flex justify-start lg:justify-end"
                                    data-testid={`${testId}-status-slot`}
                                >
                                    {statusPill}
                                </div>
                            )}
                            {rightSlot && (
                                <div
                                    className="flex items-center justify-start lg:justify-end"
                                    data-testid={`${testId}-right-slot`}
                                >
                                    {rightSlot}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Zone B only rendered when we have chips. Otherwise the
                shell collapses to a pure identity band. */}
            {chips.length > 0 && (
                <>
                    {/* Hair-thin teal gradient rule joining the two zones. */}
                    <div
                        aria-hidden
                        className="relative h-px"
                        style={{
                            background: "linear-gradient(to right, transparent 0%, rgba(20,184,166,0.32) 15%, rgba(20,184,166,0.32) 85%, transparent 100%)",
                        }}
                    />

                    {/* ═══════════════════════════════════════════════════════
                        ZONE B — Cockpit data strip
                        ─────────────────────────────────────────────────────── */}
                    <div
                        className="relative"
                        style={{
                            background: "linear-gradient(to bottom, rgba(20,184,166,0.045) 0%, rgba(20,184,166,0.025) 100%)",
                        }}
                        data-testid={`${testId}-data-strip`}
                    >
                        <div className={`grid ${gridCols} divide-y divide-teal-200/40 sm:divide-y-0 sm:divide-x sm:divide-teal-200/40`}>
                            {chips.map((chip, i) => (
                                chip.render
                                    ? <React.Fragment key={i}>{chip.render}</React.Fragment>
                                    : <DataCell key={i} {...chip} />
                            ))}
                        </div>
                        {stripe}
                    </div>
                </>
            )}
        </div>
    );
}
